import { useRef, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { formatNumber } from '../form-targets';
import { sliderRange, sliderStepOf, sliderSteps, sliderValueAt } from './numeric';
import type { WidgetProps } from './types';

/** Déplacement horizontal (px) au-delà duquel un appui devient un glissé. */
const DRAG_THRESHOLD_PX = 4;
/** Crans d'un saut « Page précédente / suivante ». */
const PAGE_STEPS = 10;

interface Drag {
  pointerId: number;
  startX: number;
  startValue: number | null;
  moved: boolean;
}

/**
 * Curseur `slider` d'une mesure (ADR-146 §6.1), comme la tablette (`slider_field.dart`) : bornes
 * `validation.min/max` quand le champ déclare les deux (une mesure hors cible reste saisissable), sinon
 * celles de la cible ; sans bornes finies, le rendu du type. Pas : 1 pour un entier, sinon le dixième ou
 * la précision des bornes, mille crans au plus. La valeur exacte s'affiche avec son unité.
 *
 * Défilement : la page défile sans bouger le curseur. Aucune réaction à la molette ; au doigt, la piste
 * laisse le défilement vertical au navigateur (`touch-action: pan-y`) ; un appui ne change la valeur
 * qu'au relâcher (toucher) ou sur un glissé horizontal ; un geste repris par le défilement
 * (`pointercancel`) rend la valeur d'avant. Clavier : flèches, Page précédente / suivante, Début, Fin.
 */
export function Slider({ id, field, value, onChange, disabled, invalid, describedBy, target, validation, unit: unitSymbol, step }: WidgetProps): ReactNode {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  if (field.type !== 'number' && field.type !== 'decimal' && field.type !== 'integer') return null;
  const range = sliderRange(validation, target);
  if (range === null) return null;
  const { min, max } = range;
  const { decimals, divisions, unit: notch } = sliderSteps(field.type, min, max, step);
  const current = typeof value === 'number' && Number.isFinite(value) ? value : null;
  const stepIndex = current === null ? null : Math.min(divisions, Math.max(0, sliderStepOf(min, decimals, Math.min(max, Math.max(min, current)), notch)));
  const valueOf = (index: number): number => sliderValueAt(min, decimals, Math.min(divisions, Math.max(0, index)), notch);
  const unit = unitSymbol === undefined || unitSymbol === '' ? '' : ` ${unitSymbol}`;
  const shown = current === null ? 'Aucune valeur' : `${formatNumber(current)}${unit}`;
  const ratio = stepIndex === null ? 0 : stepIndex / divisions;

  const valueAt = (clientX: number): number | null => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) return null;
    const position = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return valueOf(Math.round(position * divisions));
  };
  const commit = (next: number | null): void => {
    if (next !== null && next !== current) onChange(next);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (disabled || event.button !== 0) return;
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startValue: current, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    if (!drag.moved && Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    commit(valueAt(event.clientX));
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    commit(valueAt(event.clientX));
    document.getElementById(id)?.focus();
  };
  const onPointerCancel = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    // Le navigateur a pris le geste pour défiler : la valeur d'avant revient.
    if (drag.moved && drag.startValue !== current) onChange(drag.startValue);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const from = stepIndex ?? 0;
    let next: number | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = stepIndex === null ? min : valueOf(from + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = stepIndex === null ? min : valueOf(from - 1);
        break;
      case 'PageUp':
        next = valueOf(from + PAGE_STEPS);
        break;
      case 'PageDown':
        next = valueOf(from - PAGE_STEPS);
        break;
      case 'Home':
        next = min;
        break;
      case 'End':
        next = valueOf(divisions);
        break;
      default:
        return;
    }
    event.preventDefault();
    commit(next);
  };

  return (
    <div className={`ay-slider${disabled ? ' ay-slider--disabled' : ''}`} data-invalid={invalid ? 'true' : undefined}>
      <div className="ay-slider__row">
        <div ref={trackRef} className="ay-slider__track" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} data-testid={`${id}-track`}>
          <span className="ay-slider__fill" style={{ inlineSize: `${ratio * 100}%` }} aria-hidden="true" />
          <div
            id={id}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            className={`ay-slider__thumb${current === null ? ' ay-slider__thumb--empty' : ''}`}
            style={{ insetInlineStart: `${ratio * 100}%` }}
            aria-labelledby={`${id}-label`}
            aria-describedby={describedBy}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={current ?? min}
            aria-valuetext={shown}
            aria-invalid={invalid}
            aria-disabled={disabled || undefined}
            aria-orientation="horizontal"
            onKeyDown={onKeyDown}
          />
        </div>
        <output className="ay-slider__value" htmlFor={id} aria-live="polite">
          {shown}
        </output>
      </div>
      <div className="ay-slider__bounds" aria-hidden="true">
        <span>{`${formatNumber(min)}${unit}`}</span>
        <span>{`${formatNumber(max)}${unit}`}</span>
      </div>
    </div>
  );
}
